// NANP-only phone normalisation, shared by the inbound webhook, the
// send-worker, the POS importer and the contacts UI. The canonical form is
// bare-10 national digits (matching customers.customer_phone / allorders),
// converted to E.164 only at the provider boundary. Twin of the SQL function
// `sms_normalize_phone` in 20260909195104_sms_contacts_customers_key.sql —
// keep both in sync.

/** Test vectors shared with src/test/smsPhone.test.ts and the SQL twin. */
export const PHONE_TEST_VECTORS: ReadonlyArray<[input: string | null | undefined, expected: string | null]> = [
  ["+12093620317", "2093620317"],
  ["2099104455", "2099104455"],
  ["(209) 910-4455", "2099104455"],
  ["12099104455", "2099104455"],
  ["0000274100", null],
  ["0006274100", null], // real allorders.customer_phone junk placeholder, 2,994 orders
  ["0096274100", null],
  ["1231231234", null],
  ["9090909090", null],
  ["1502290678", null],
  ["5555555555", null],
  ["", null],
  [null, null],
  ["+44 20 7946 0958", null],
];

/**
 * Normalises a raw phone string to bare-10 national digits, accepting only
 * valid NANP numbers. Returns null for anything else: non-NANP numbers,
 * junk placeholders (repeated-digit numbers), or invalid area/exchange
 * codes (first digit of area code and exchange must be 2-9).
 */
export function normalizeToNational10(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  let digits = raw.replace(/[^0-9]/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length !== 10) return null;
  if (!/^[2-9][0-9]{2}[2-9][0-9]{6}$/.test(digits)) return null;
  if (/^(\d)\1{9}$/.test(digits)) return null;
  return digits;
}

/** Converts a bare-10 national number to E.164 for the provider boundary. */
export function toE164(national10: string): string {
  return `+1${national10}`;
}

/** Formats a bare-10 national number for display: "2099104455" -> "(209) 910-4455". */
export function formatForDisplay(national10: string): string {
  return `(${national10.slice(0, 3)}) ${national10.slice(3, 6)}-${national10.slice(6)}`;
}
